import { useMemo } from 'react';

import { useApi } from '@/shared/api/hooks/useApi';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';

import { InterwillDto } from '../types';

export const useInterwillsActions = () => {
  const keyPair = useKeyPairStore((s) => s.keyPair);
  const api = useApi();
  const sjDb = useSJDatabase();

  return useMemo(() => {
    const create = async (): Promise<InterwillDto | null> => {
      if (!keyPair?.publicKey) {
        throw new Error('[useInterwillsActions] empty publicKey');
      }

      let interwillDbId: string | undefined;
      const resFindAll = await api.willsFindAll({});
      interwillDbId = resFindAll.data?.[0]?.id;

      if (!interwillDbId) {
        const resCreate = await api.willsCreate({});
        interwillDbId = resCreate.data?.id;
      }

      const dto: InterwillDto = {
        id: sjDb.generateId(CollectionName.INTERWILLS),
        centralDbInterwillId: interwillDbId,
      };

      await sjDb.collections.interwills?.create(dto, [keyPair.publicKey]);

      return dto;
    };

    const update = async (data: InterwillDto): Promise<InterwillDto | null> => {
      const res = await sjDb.collections.interwills?.update(data);

      return res ?? null;
    };

    const getInterwill = async (): Promise<InterwillDto> => {
      let item: InterwillDto | null;

      await sjDb.awaitInited;
      item = sjDb.collections.interwills?.findOne(() => true) ?? null;

      if (!item) {
        item = await create();
      }

      if (!item) {
        throw new Error('[useInterwillsActions] getInterwill failed');
      }

      return item;
    };

    const remove = async () => {
      const will = await getInterwill();
      if (!will) return null;

      if (!will.centralDbInterwillId) {
        throw new Error(`[useInterwillsActions] centralDbInterwillId not specified`);
      }
      sjDb.collections.interwills?.remove(will);
      return api.willsRemove(will.centralDbInterwillId);
    };

    const recreate = async () => {
      const will = await getInterwill();

      if (will.centralDbInterwillId) {
        await api.willsRemove(will.centralDbInterwillId);
      }
      const resCreate = await api.willsCreate({});

      return await update({
        ...will,
        centralDbInterwillId: resCreate.data?.id,
      });
    };

    return { recreate, update, remove, getInterwill };
  }, [keyPair, api, sjDb]);
};
